/* oxlint-disable anti-slop/no-chained-type-assertions, anti-slop/no-known-value-widening, anti-slop/no-runtime-typeof, anti-slop/no-unknown-parameters, anti-slop/no-unsafe-dictionary-type, anti-slop/require-safety-comment-for-type-assertion */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import './BotDetails.css';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import {
  Toast,
  ToastContainer,
  Card,
  Button,
  Modal,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import { Link, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import PositionsList from '../components/PositionsList';
import OrdersList from '../components/OrdersList';
import ResultsChart from '../components/ResultsChart';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faCopy,
  faLock,
  faCheckCircle,
  faClock,
  faRefresh,
  faChevronUp,
  faChevronDown,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { NumericFormat } from 'react-number-format';
import useUser from '../hooks/useUser';
import { pushEvent } from '../utils/analytics';
import { buildAuthOverlayRouteState } from '../utils/authOverlayNavigation';
import { StrategyActionButton } from '../components/StrategyActions';
import StrategyAutomationBadge from '../components/StrategyAutomationBadge';
import DeployLogs from '../components/DeployLogs';
import ShareStrategyModal from '../components/ShareStrategyModal/ShareStrategyModal';
import { fetchWithAuth } from '../utils/apiHelpers';
import {
  formatDeploymentSummaryStatus,
  isDeploymentScheduleStopped,
} from '../utils/deploymentScheduleStatus';
import { getBrokerAccountLabel, getBrokerConnectionTitle } from '../utils/brokerConnectionDisplay';
import { navigateToAiAgent } from '../utils/aiAgentNav';
import { safeGetStorageItem } from '../utils/browserStorage';
import { PageShell } from '../design-system';
import { useStrategyMarketplaceShare } from '../hooks/useStrategyMarketplaceShare';
import type { DeploymentSchedule, StrategyRecord } from '../types/account';
import { buildDeploymentFixWithAiHandoff } from '../utils/aiRecoveryContracts';
import { formatLiveReturnPercentage } from '../utils/liveReturns';

const STOP_BOT_POSITION_ACKNOWLEDGEMENT =
  'I understand stopping this bot only stops future automated trading changes. It will not sell, close, or liquidate any positions already in my brokerage account, and I am responsible for reviewing open positions and orders with my broker.';

const getStoredCustomAuthToken = () => safeGetStorageItem('localStorage', 'customAuthToken');

type TimeframeKey =
  | 'return24h'
  | 'return7d'
  | 'return30d'
  | 'return3mo'
  | 'return6mo'
  | 'return1y'
  | 'return2y'
  | 'returnAll';

interface BrokerCredential {
  id?: string;
  broker?: string;
  displayName?: string;
  paperOrLive?: 'paper' | 'live' | string;
  status?: string;
  authMethod?: string;
  externalAccountId?: string;
  externalAccountLabel?: string;
}

interface DeploymentStatusSummary {
  id?: string;
  botId?: string;
  status?: string;
  managerBotId?: string | null;
  manager_bot_id?: string | null;
  managerStatus?: string | null;
  manager_status?: string | null;
  deploymentPath?: string | null;
  deployment_path?: string | null;
  routingReason?: string | null;
  routing_reason?: string | null;
  liveStatus?: string | null;
  live_status?: string | null;
  errorDetails?: string | null;
  error_details?: string | null;
  schedule?: DeploymentSchedule | null;
  createdAt?: string;
  created_at?: string;
  startedAt?: string;
  started_at?: string;
  startDate?: string;
  start_date?: string;
  updatedAt?: string;
  updated_at?: string;
  brokerCredential?: BrokerCredential | null;
  live?: {
    account?: AccountSnapshot | null;
  };
  strategy?: {
    id?: string;
    aiStrategyId?: string;
    name?: string;
  };
  strategyId?: string;
  strategy_id?: string;
  aiStrategyRevisionId?: string;
  revisionVersion?: string | number | null;
  aiEnabled?: boolean | null;
  deploymentMode?: 'scheduled' | 'always_on' | string | null;
  telemetryBotId?: string | null;
  performanceProfileId?: string;
  performanceProfileSourceId?: string;
  performanceProfileStartedAt?: string;
  performanceProfileSourceStartedAt?: string;
}

interface AccountSnapshot {
  found?: boolean;
  latestPortfolioValue?: number | string | null;
  latest_portfolio_value?: number | string | null;
  latestCash?: number | string | null;
  latest_cash?: number | string | null;
  latestEventTimestamp?: string | null;
  latest_event_timestamp?: string | null;
}

interface BotDetailsTelemetryMetadata {
  account?: { source?: string | null; updatedAt?: string | null; freshness?: string | null };
  positions?: { source?: string | null; updatedAt?: string | null; freshness?: string | null };
  history?: { performanceProfileId?: string | null; source?: string | null };
}

interface PerformanceStats {
  timeframe?: TimeframeKey | string;
  stats?: {
    return?: number;
    total_return?: number;
    maxDrawdown?: number;
    volatility?: number;
    is_data_complete?: boolean;
    [key: string]: unknown;
  };
}

interface BotDetailsData {
  id: string;
  name?: string;
  description?: string;
  apiKey?: string | null;
  isOwner?: boolean;
  isRealMoney?: boolean;
  aiEnabled?: boolean | null;
  owner?: {
    id?: string;
    nickname?: string;
  };
  strategy?: {
    id?: string;
    name?: string;
  };
  strategyId?: string;
  strategy_id?: string;
  activeDeployment?: DeploymentStatusSummary | null;
  latestAccountSnapshot?: AccountSnapshot | null;
  performanceStats?: PerformanceStats[];
  positions?: unknown[];
  performanceProfileId?: string;
  performanceProfileSourceId?: string;
  performanceProfileStartedAt?: string;
  performanceProfileSourceStartedAt?: string;
  performanceStartDate?: string;
  createdAt?: string;
  created_at?: string;
  startedAt?: string;
  started_at?: string;
  startDate?: string;
  start_date?: string;
  botMode?: string;
  last_updated?: string;
  botDetailsTelemetry?: BotDetailsTelemetryMetadata;
  [key: string]: unknown;
}

interface ChartPoint {
  date: number;
  portfolio_value?: number;
  cash_adjusted_portfolio_value?: number | null;
  cash?: number;
  spy_benchmark?: number;
}

interface HistoricalReturnItem {
  timestamp: string | number | Date;
  portfolio_value?: number;
  cash_adjusted_portfolio_value?: number | null;
  cash?: number;
}

interface ChartMarker {
  id: string;
  marker_type: 'deposit' | 'withdrawal' | 'deployment_start' | 'deployment_stop';
  timestamp: string;
  amount?: number;
  currency?: string | null;
  description?: string | null;
  revision_version?: number | null;
  deployment_mode?: string | null;
  cadence?: string | null;
}

interface ChartGap {
  id: string;
  gap_type: string;
  start: string;
  end: string;
}

interface CashFlowAvailability {
  status: 'available' | 'unsupported' | 'unavailable';
  provider: string | null;
  reason: string | null;
}

interface BenchmarkPoint {
  date: string | number | Date;
  spy_benchmark: number;
}

interface DeploymentOrderMeta {
  lastUpdated: string | null;
  source: string | null;
}

interface MarketplaceListing {
  id: string;
}

interface TimeframeConfig {
  frequency: 'day' | 'hour';
  label: string;
}

interface DeploymentOrdersResponse {
  orders?: unknown[];
  lastUpdated?: string | null;
  last_updated?: string | null;
  source?: string | null;
  telemetryStatus?: 'absent' | 'empty' | 'present' | 'unavailable' | string | null;
}

interface DeploymentLiveStateResponse {
  deploymentId: string;
  status?: string | null;
  schedule?: DeploymentSchedule | null;
  account?: AccountSnapshot | null;
  positions?: {
    items?: unknown[];
    count?: number;
    lastUpdated?: string | null;
    currentVersionId?: string | null;
  } | null;
  orders?: DeploymentOrdersResponse | null;
  source?: {
    selected?: string | null;
    observedAt?: string | null;
    orders?: string | null;
    ordersObservedAt?: string | null;
  } | null;
  generatedAt?: string | null;
}

interface HistoricalReturnsOptions {
  requestKey?: string;
  clearOnEmpty?: boolean;
}

interface HistoricalReturnsResponse {
  items?: HistoricalReturnItem[];
  latest_event_timestamp?: string | number | Date;
  is_new_bot?: boolean;
  is_new_profile?: boolean;
  latest_portfolio_value?: number | string | null;
  latest_cash?: number | string | null;
  adjusted_return?: number | null;
  cash_flow_markers?: ChartMarker[];
  timeline_markers?: ChartMarker[];
  data_gaps?: ChartGap[];
  cash_flow_availability?: CashFlowAvailability;
}

interface MarketplaceCheckResponse {
  isPublished?: boolean;
  listingId?: string;
}

interface BotStatusPanelProps {
  variant?: 'info' | 'deploying' | 'waiting' | 'stopping' | 'failed';
  title: string;
  body: string;
  icon?: IconDefinition;
  spin?: boolean;
  details?: string[];
  action?: {
    label: string;
    kind?: 'default' | 'ai';
    onClick?: () => void;
    href?: string;
  };
}

interface StrategyActionButtonProps {
  actionType: 'editStrategy' | 'shareStrategy' | 'startTrading' | 'runBacktest';
  size?: 'compact' | 'mini' | 'medium' | 'large' | 'hero';
  label: string;
  onClick: () => void | Promise<void>;
  type?: 'button';
  disabled?: boolean;
}

interface ResultsChartProps {
  data: ChartPoint[] | null;
  frequency: TimeframeConfig['frequency'];
  isLoading: boolean;
  trendDirection: 'neutral' | 'up' | 'down';
  minimal?: boolean;
  chartHeight: number;
  emptyTitle?: string;
  emptyDescription?: string;
  markers?: ChartMarker[];
  cashFlowAvailability?: CashFlowAvailability | null;
  isStale?: boolean;
}

interface DeployLogsProps {
  deployId: string;
  getApiToken: () => Promise<string | null>;
  expanded: boolean;
  onToggle: () => void;
  hideToggleButton?: boolean;
}

interface PositionsListProps {
  positions: unknown[];
}

interface OrdersListProps {
  orders: unknown[];
  isLoading?: boolean;
  error?: string;
  lastUpdated?: string | null;
  source?: string | null;
  onRefresh?: () => void;
}

type ToastVariant = 'success' | 'danger' | 'warning' | 'info';

const BotStrategyActionButton =
  StrategyActionButton as React.ComponentType<StrategyActionButtonProps>;
const BotResultsChart = ResultsChart as React.ComponentType<ResultsChartProps>;
const BotDeployLogs = DeployLogs as unknown as React.ComponentType<DeployLogsProps>;
const BotPositionsList = PositionsList as React.ComponentType<PositionsListProps>;
const BotOrdersList = OrdersList as React.ComponentType<OrdersListProps>;

const BOT_ANALYTICS_EVENTS = {
  detailsViewed: 'bot_details_viewed',
  detailsError: 'bot_details_error',
  stopRequested: 'bot_stop_requested',
  stopError: 'bot_stop_error',
  chartPeriodChanged: 'bot_chart_period_changed',
} as const;

const TIMEFRAME_ORDER = [
  'return24h',
  'return30d',
  'return3mo',
  'return6mo',
  'return1y',
  'returnAll',
] as const satisfies readonly TimeframeKey[];

function normalizeTimeframeParam(tf: string | null): TimeframeKey {
  if (!tf || tf === 'allTime') {
    return 'returnAll';
  }
  return (TIMEFRAME_ORDER as readonly string[]).includes(tf) ? (tf as TimeframeKey) : 'returnAll';
}

export function getDefaultTimeframeForBot(
  theBot?: BotDetailsData | null,
  nowMs: number = Date.now(),
): TimeframeKey {
  const validDates = (values: unknown[]) =>
    values
      .map((value) => (value ? Date.parse(String(value)) : NaN))
      .filter((value) => Number.isFinite(value) && value <= nowMs);
  // A newly created deployment/profile can already contain years of account
  // history. Use the earliest history evidence, not the first populated field.
  const historyDates = validDates([
    theBot?.performanceProfileStartedAt,
    theBot?.activeDeployment?.performanceProfileStartedAt,
    theBot?.performanceProfileSourceStartedAt,
    theBot?.activeDeployment?.performanceProfileSourceStartedAt,
    theBot?.performanceStartDate,
    ...(theBot?.performanceStats || [])
      .filter((row) => row.timeframe === 'returnAll')
      .map((row) => row.stats?.start_date),
  ]);
  const dates = historyDates.length
    ? historyDates
    : validDates([
        theBot?.startedAt,
        theBot?.started_at,
        theBot?.startDate,
        theBot?.start_date,
        theBot?.createdAt,
        theBot?.created_at,
        theBot?.activeDeployment?.startedAt,
        theBot?.activeDeployment?.started_at,
        theBot?.activeDeployment?.startDate,
        theBot?.activeDeployment?.start_date,
        theBot?.activeDeployment?.createdAt,
        theBot?.activeDeployment?.created_at,
      ]);
  return dates.length && nowMs - Math.min(...dates) < 24 * 60 * 60 * 1000
    ? 'return24h'
    : 'returnAll';
}

function getStatsReturnPercent(stats?: PerformanceStats): number | null {
  const raw = stats?.stats?.return ?? stats?.stats?.total_return;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

type SignedMetricTone = 'positive' | 'negative' | 'neutral';

interface PortfolioValueChange {
  amount: number;
  percent: number;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'string' && value.trim() === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function getSignedMetricTone(value: number | null): SignedMetricTone {
  if (value === null || !Number.isFinite(value) || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
}

function getSignedMetricToneClass(tone: SignedMetricTone): string {
  if (tone === 'positive') return 'account-return-positive';
  if (tone === 'negative') return 'account-return-negative';
  return '';
}

function formatSignedCurrencyDelta(value: number): string {
  const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return `${prefix}${formatted}`;
}

function calculatePortfolioValueChange(
  points: ChartPoint[],
  latestPortfolioValue?: number | string | null,
): PortfolioValueChange | null {
  const portfolioPoints = points.filter((point) => toFiniteNumber(point.portfolio_value) !== null);
  if (portfolioPoints.length < 1) return null;

  const firstValue = toFiniteNumber(portfolioPoints[0].portfolio_value);
  const lastChartValue = toFiniteNumber(
    portfolioPoints[portfolioPoints.length - 1].portfolio_value,
  );
  const latestValue = toFiniteNumber(latestPortfolioValue);
  const lastValue = latestValue ?? lastChartValue;

  if (firstValue === null || lastValue === null || firstValue === 0) return null;

  const amount = lastValue - firstValue;
  return {
    amount,
    percent: (amount / firstValue) * 100,
  };
}

function formatBrokerName(value: unknown): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalized) return '';
  if (normalized === 'alpaca') return 'Alpaca';
  if (normalized === 'tradier') return 'Tradier';
  if (normalized === 'schwab') return 'Schwab';
  return normalized.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBrokerConnectionLabel(credential?: BrokerCredential | null): string {
  if (!credential) return '';
  const title = getBrokerConnectionTitle(credential);
  const name = title || credential.displayName || formatBrokerName(credential.broker);
  const mode =
    credential.paperOrLive === 'live' ? 'Live' : credential.paperOrLive === 'paper' ? 'Paper' : '';
  const label = [
    name,
    mode && !String(name).toLowerCase().includes(mode.toLowerCase()) ? mode : null,
  ]
    .filter(Boolean)
    .join(' ');
  const accountLabel = getBrokerAccountLabel(credential);
  return [label.trim(), accountLabel].filter(Boolean).join(' · ');
}

// Helper function to get a limit for the timeframe
function getLimitForTimeframe(tf: TimeframeKey, freq: TimeframeConfig['frequency']): number {
  if (freq === 'hour') {
    switch (tf) {
      case 'return24h':
        return 48; // 24 hours (maybe a little buffer)
      case 'return7d':
        return 200; // 7 days hourly is ~168
      case 'return30d':
        return 900; // 30 days hourly ~720
      default:
        return 100; // fallback
    }
  } else {
    // daily frequency
    switch (tf) {
      case 'returnAll':
        return 1000;
      case 'return7d':
        return 10; // 7 days, a bit more
      case 'return30d':
        return 40; // 30 days
      case 'return3mo':
        return 110; // ~3 months of trading days, plus buffer
      case 'return6mo':
        return 220; // ~6 months of trading days, plus buffer
      case 'return1y':
        return 400; // ~252 trading days, plus buffer
      case 'return2y':
        return 800; // ~504 trading days in 2 years, plus buffer
      default:
        return 100; // fallback
    }
  }
}

function getBotPerformanceProfileId(
  theBot?: BotDetailsData | null,
  preferredPerformanceProfileId: string | null = null,
): string | null {
  return (
    preferredPerformanceProfileId ||
    theBot?.performanceProfileId ||
    theBot?.activeDeployment?.performanceProfileId ||
    null
  );
}

function getBotPerformanceProfileSourceId(
  theBot?: BotDetailsData | null,
  preferredPerformanceProfileSourceId: string | null = null,
): string | null {
  return (
    preferredPerformanceProfileSourceId ||
    theBot?.performanceProfileSourceId ||
    theBot?.activeDeployment?.performanceProfileSourceId ||
    null
  );
}

function getBotPerformanceStartDate(theBot?: BotDetailsData | null): string {
  return (
    theBot?.performanceProfileStartedAt ||
    theBot?.activeDeployment?.performanceProfileStartedAt ||
    theBot?.performanceProfileSourceStartedAt ||
    theBot?.activeDeployment?.performanceProfileSourceStartedAt ||
    theBot?.performanceStartDate ||
    theBot?.createdAt ||
    theBot?.startedAt ||
    theBot?.startDate ||
    '2000-01-01T00:00:00Z'
  );
}

function buildBotDetailsApiUrl(
  apiBaseUrl: string | undefined,
  botId: string,
  {
    deploymentId,
    performanceProfileId,
    performanceProfileSourceId,
  }: {
    deploymentId?: string | null;
    performanceProfileId?: string | null;
    performanceProfileSourceId?: string | null;
  } = {},
): string {
  const params = new URLSearchParams();
  if (deploymentId) {
    params.set('deployment', deploymentId);
  }
  if (performanceProfileId) {
    params.set('performanceProfileId', performanceProfileId);
  }
  if (performanceProfileSourceId) {
    params.set('performanceProfileSourceId', performanceProfileSourceId);
  }
  const query = params.toString();
  return `${apiBaseUrl}/bots/${encodeURIComponent(botId)}${query ? `?${query}` : ''}`;
}

function normalizeChartTimestamp(
  ts: string | number | Date,
  freq: TimeframeConfig['frequency'],
): number {
  const d = new Date(ts);
  if (freq === 'day') {
    d.setUTCHours(0, 0, 0, 0);
  } else if (freq === 'hour') {
    d.setUTCMinutes(0, 0, 0);
  }
  return d.getTime();
}

export function fillBenchmarkEdges(
  portfolioData: ChartPoint[],
  benchData: ChartPoint[],
): ChartPoint[] {
  if (!benchData.length || !portfolioData.length) return benchData;
  const earliestPort = portfolioData[0].date;
  const latestPort = portfolioData[portfolioData.length - 1].date;
  const sortedBenchData = [...benchData]
    .filter((point) => Number.isFinite(point.date))
    .sort((left, right) => left.date - right.date);
  const pointsBeforeStart = sortedBenchData.filter((point) => point.date <= earliestPort);
  const beforeStart = pointsBeforeStart[pointsBeforeStart.length - 1];
  const afterEnd = sortedBenchData.find((point) => point.date >= latestPort);
  const nextBenchData = sortedBenchData.filter(
    (point) => point.date >= earliestPort && point.date <= latestPort,
  );

  const startSource = beforeStart || nextBenchData[0] || afterEnd;
  if (startSource && (!nextBenchData.length || nextBenchData[0].date > earliestPort)) {
    nextBenchData.unshift({ ...startSource, date: earliestPort });
  }

  const lastPoint = nextBenchData[nextBenchData.length - 1];
  const endSource = afterEnd || lastPoint || beforeStart;
  if (endSource && (!lastPoint || lastPoint.date < latestPort)) {
    nextBenchData.push({ ...endSource, date: latestPort });
  }
  return nextBenchData;
}

function scaleBenchmark(portfolioData: ChartPoint[], benchData: ChartPoint[]): ChartPoint[] {
  if (!portfolioData.length || !benchData.length) return [];
  const firstPortVal = portfolioData[0].portfolio_value || 0;
  const firstBenchVal = benchData[0].spy_benchmark;
  if (!firstBenchVal || !firstPortVal) return benchData;

  const ratio = firstPortVal / firstBenchVal;
  return benchData.map((b) => ({
    ...b,
    spy_benchmark: typeof b.spy_benchmark === 'number' ? b.spy_benchmark * ratio : undefined,
  }));
}

function mergeChartData(portData: ChartPoint[], benchData: ChartPoint[]): ChartPoint[] {
  const map = new Map<number, ChartPoint>();
  for (const p of portData) {
    map.set(p.date, { ...p });
  }
  for (const b of benchData) {
    if (!map.has(b.date)) {
      map.set(b.date, { date: b.date });
    }
    const point = map.get(b.date);
    if (point) {
      point.spy_benchmark = b.spy_benchmark;
    }
  }
  return Array.from(map.values()).sort((a, b) => a.date - b.date);
}

async function fetchBenchmarkData(
  ticker: string,
  startDate: Date,
  endDate: Date,
  interval: TimeframeConfig['frequency'],
): Promise<BenchmarkPoint[]> {
  const period1 = Math.floor(startDate.getTime() / 1000);
  const period2 = Math.floor(endDate.getTime() / 1000);
  const url = `${process.env.REACT_APP_API_URL}/benchmarks/historical?symbol=${encodeURIComponent(
    ticker,
  )}&start=${period1}&end=${period2}&interval=${interval}`;

  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch benchmark, status ${resp.status}`);
  return (await resp.json()) as BenchmarkPoint[];
}

const DEPLOYMENT_PENDING_STATUSES = [
  'scheduled',
  'initiated',
  'queued',
  'in_progress',
  'building',
  'dependency_building',
  'deploying',
  'pulling',
  'starting',
  'waiting_for_capacity',
];

const DEPLOYMENT_RUNNING_STATUSES = ['running', 'running_once'];

const DEPLOYMENT_COMPLETED_STATUSES = ['completed'];

const DEPLOYMENT_COMPLETED_WITH_ERRORS_STATUSES = ['completed_with_errors'];

const DEPLOYMENT_STOPPING_STATUSES = [
  'stopping',
  'stop_requested',
  'stopping_requested',
  'terminating',
];

const DEPLOYMENT_STOPPED_STATUSES = [
  'stopped',
  'force_stopped',
  'terminated',
  'terminated_timeout',
  'deleted',
  'bot_deleted',
];

const DEPLOYMENT_STOP_FAILED_STATUSES = ['stop_failed'];

function mergeDeploymentSchedule(
  next?: DeploymentSchedule | null,
  current?: DeploymentSchedule | null,
): DeploymentSchedule | null {
  if (!next && !current) return null;
  return {
    ...(current || {}),
    ...(next || {}),
  };
}

const preserveCurrentSchedule = (
  next?: DeploymentSchedule | null,
  current?: DeploymentSchedule | null,
): DeploymentSchedule | null => mergeDeploymentSchedule(current, next);

/**
 * BotDetails:
 * - Accounts with less than one day of history default to hourly "return24h".
 * - Existing account histories default to "returnAll", including after restart.
 * - An explicit URL or user selection overrides the history-aware default.
 * - Sparse all-time data should stay all-time instead of silently rewriting the URL.
 * - Timeframes align with My Bots so list/detail returns don't disagree.
 */

export default function BotDetails() {
  const { getApiToken, isAuthenticated, isLoading: isAuthLoading } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const botId = id || '';
  const botDetailsReturnTo = `${location.pathname}${location.search}${location.hash || ''}`;
  const botDetailsLoginHref = `/login?returnTo=${encodeURIComponent(botDetailsReturnTo || `/bot/${botId}`)}`;
  const handleLoginToBotDetails = useCallback(() => {
    navigate(botDetailsLoginHref, {
      state: buildAuthOverlayRouteState(location),
    });
  }, [botDetailsLoginHref, location, navigate]);

  const getBotDetailsApiToken = useCallback(async (): Promise<string | null> => {
    const token = isAuthenticated ? await getApiToken() : null;
    return token || getStoredCustomAuthToken();
  }, [getApiToken, isAuthenticated]);

  const searchParamsString = searchParams.toString();
  const requestedTimeframeParam = searchParams.get('timeframe');
  const initialTimeframe = requestedTimeframeParam
    ? normalizeTimeframeParam(requestedTimeframeParam)
    : 'returnAll';

  const [botDetails, setBotDetails] = useState<BotDetailsData | null>(null);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [chartDataIsFresh, setChartDataIsFresh] = useState(true);
  const [chartMarkers, setChartMarkers] = useState<ChartMarker[]>([]);
  const [cashFlowAvailability, setCashFlowAvailability] = useState<CashFlowAvailability | null>(
    null,
  );
  const [adjustedReturnPercent, setAdjustedReturnPercent] = useState<number | null>(null);
  const [botStats, setBotStats] = useState<PerformanceStats | undefined>(undefined);
  const [timeframe, setTimeframe] = useState<TimeframeKey>(initialTimeframe);
  const [fallbackApiKey] = useState<string | null>(null);
  const [latestPortfolioValue, setLatestPortfolioValue] = useState<number | string | null>(null);
  const [latestCash, setLatestCash] = useState<number | string | null>(null);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [botDetailsError, setBotDetailsError] = useState<Error | null>(null);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToastVariant>('success');
  const [saved, setSaved] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [isStoppingBot, setIsStoppingBot] = useState(false);
  const [acceptedStopPositionAcknowledgement, setAcceptedStopPositionAcknowledgement] =
    useState(false);
  const [hasEverHadData, setHasEverHadData] = useState(false);
  const [deploymentSummary, setDeploymentSummary] = useState<DeploymentStatusSummary | null>(null);
  const [deploymentPositions, setDeploymentPositions] = useState<unknown[] | null>(null);
  const [deploymentPositionsUpdatedAt, setDeploymentPositionsUpdatedAt] = useState<string | null>(
    null,
  );
  const [deploymentOrders, setDeploymentOrders] = useState<unknown[]>([]);
  const [deploymentOrdersMeta, setDeploymentOrdersMeta] = useState<DeploymentOrderMeta>({
    lastUpdated: null,
    source: null,
  });
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [logsExpanded, setLogsExpanded] = useState(false);

  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [marketplaceListing, setMarketplaceListing] = useState<MarketplaceListing | null>(null);
  const [isLoadingMarketplaceListing, setIsLoadingMarketplaceListing] = useState(false);
  const {
    closeShareStrategy,
    existingPrivateShare,
    makePrivate: makeBackingStrategyPrivate,
    makePublic: makeBackingStrategyPublic,
    openShareStrategy,
    refreshShareInfo,
    shareInfoLoading,
    shareStrategy,
  } = useStrategyMarketplaceShare({
    onStrategyMarketplaceChange: (_strategy, marketplace) => {
      setMarketplaceListing(
        marketplace?.isInMarketplace && marketplace.marketplaceListingId
          ? { id: marketplace.marketplaceListingId }
          : null,
      );
    },
  });

  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingRealPaper, setEditingRealPaper] = useState(false);
  const [newRealPaper, setNewRealPaper] = useState(false);

  const deploymentIdFromUrl = searchParams.get('deployment');
  const performanceProfileIdFromUrl = searchParams.get('performanceProfileId') || null;
  const performanceProfileSourceIdFromUrl = searchParams.get('performanceProfileSourceId') || null;

  // Add auto-refresh interval ref
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveStatePollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStateInFlightRef = useRef(false);
  const liveStateFailureStreakRef = useRef(0);
  const botDetailsRef = useRef<BotDetailsData | null>(null);
  const chartRequestKeyRef = useRef('');

  useEffect(() => {
    botDetailsRef.current = botDetails;
  }, [botDetails]);

  const applyLatestAccountSnapshot = useCallback((snapshot?: AccountSnapshot | null): boolean => {
    if (!snapshot) return false;

    const portfolioValue = snapshot.latestPortfolioValue ?? snapshot.latest_portfolio_value ?? null;
    const cash = snapshot.latestCash ?? snapshot.latest_cash ?? null;

    if (portfolioValue === null && cash === null) {
      return false;
    }

    if (portfolioValue !== null) {
      setLatestPortfolioValue(portfolioValue);
    }
    if (cash !== null) {
      setLatestCash(cash);
    }
    return true;
  }, []);

  const fetchCurrentBotDetails = useCallback(
    async (token: string | null): Promise<BotDetailsData> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const url = buildBotDetailsApiUrl(process.env.REACT_APP_API_URL, botId, {
        deploymentId: deploymentIdFromUrl,
        performanceProfileId: performanceProfileIdFromUrl,
        performanceProfileSourceId: performanceProfileSourceIdFromUrl,
      });
      const resp = await fetch(url, { headers });
      let data: Partial<BotDetailsData> | null = null;
      try {
        data = await resp.json();
      } catch (jsonErr) {
        data = null;
      }

      if (!resp.ok) {
        const message = String(data?.message || `Failed to fetch bot details (${resp.status})`);
        const error = new Error(message) as Error & { status?: number; code?: string };
        error.status = resp.status;
        // Private bots return 403. Missing ids return 404. Keep both off toast path.
        if (resp.status === 403 || /private/i.test(message)) {
          error.code = 'private_bot';
        } else if (resp.status === 404) {
          error.code = 'bot_not_found';
        }
        throw error;
      }

      return data as BotDetailsData;
    },
    [botId, deploymentIdFromUrl, performanceProfileIdFromUrl, performanceProfileSourceIdFromUrl],
  );

  const applyBotDetailsData = useCallback(
    (data: BotDetailsData): void => {
      setBotDetails(data);
      setDeploymentSummary((current) => {
        const nextDeployment = data.activeDeployment || null;
        if (!nextDeployment) return null;
        if (current?.id && nextDeployment.id && current.id !== nextDeployment.id) {
          return nextDeployment;
        }

        return {
          ...nextDeployment,
          managerBotId:
            nextDeployment.managerBotId ??
            nextDeployment.manager_bot_id ??
            current?.managerBotId ??
            current?.manager_bot_id ??
            null,
          manager_bot_id:
            nextDeployment.manager_bot_id ??
            nextDeployment.managerBotId ??
            current?.manager_bot_id ??
            current?.managerBotId ??
            null,
          managerStatus:
            nextDeployment.managerStatus ??
            nextDeployment.manager_status ??
            current?.managerStatus ??
            current?.manager_status ??
            null,
          manager_status:
            nextDeployment.manager_status ??
            nextDeployment.managerStatus ??
            current?.manager_status ??
            current?.managerStatus ??
            null,
          deploymentPath:
            nextDeployment.deploymentPath ??
            nextDeployment.deployment_path ??
            current?.deploymentPath ??
            current?.deployment_path ??
            null,
          deployment_path:
            nextDeployment.deployment_path ??
            nextDeployment.deploymentPath ??
            current?.deployment_path ??
            current?.deploymentPath ??
            null,
          routingReason:
            nextDeployment.routingReason ??
            nextDeployment.routing_reason ??
            current?.routingReason ??
            current?.routing_reason ??
            null,
          routing_reason:
            nextDeployment.routing_reason ??
            nextDeployment.routingReason ??
            current?.routing_reason ??
            current?.routingReason ??
            null,
          liveStatus:
            nextDeployment.liveStatus ??
            nextDeployment.live_status ??
            current?.liveStatus ??
            current?.live_status ??
            null,
          live_status:
            nextDeployment.live_status ??
            nextDeployment.liveStatus ??
            current?.live_status ??
            current?.liveStatus ??
            null,
          schedule: preserveCurrentSchedule(nextDeployment.schedule, current?.schedule),
        };
      });
      const hasDeploymentLiveState = Boolean(deploymentIdFromUrl || data.activeDeployment?.id);
      if (!hasDeploymentLiveState) {
        const appliedSnapshot = applyLatestAccountSnapshot(data.latestAccountSnapshot);
        if (!appliedSnapshot) {
          applyLatestAccountSnapshot(data.activeDeployment?.live?.account);
        }
      }

      if ((data.performanceStats?.length || 0) > 0) {
        setHasEverHadData(true);
      }
    },
    [applyLatestAccountSnapshot, deploymentIdFromUrl],
  );

  // Our timeframes
  const timeframesMap = useMemo<Record<TimeframeKey, TimeframeConfig>>(
    () => ({
      returnAll: { frequency: 'day', label: 'ALL' },
      return24h: { frequency: 'hour', label: '1D' },
      return7d: { frequency: 'day', label: '7D' },
      return30d: { frequency: 'day', label: '1M' },
      return3mo: { frequency: 'day', label: '3M' },
      return6mo: { frequency: 'day', label: '6M' },
      return1y: { frequency: 'day', label: '1Y' },
      return2y: { frequency: 'day', label: '2Y' },
    }),
    [],
  );
  const selectableTimeframes = TIMEFRAME_ORDER;

  const chartSourceKey = useMemo(() => {
    if (!botDetails?.id) return '';
    const profileId = getBotPerformanceProfileId(botDetails, performanceProfileIdFromUrl) || '';
    const profileSourceId =
      getBotPerformanceProfileSourceId(botDetails, performanceProfileSourceIdFromUrl) || '';
    const deploymentId = deploymentIdFromUrl || botDetails?.activeDeployment?.id || '';
    const startedAt = getBotPerformanceStartDate(botDetails) || '';
    return [botDetails.id, deploymentId, profileId, profileSourceId, startedAt].join('|');
  }, [
    botDetails,
    deploymentIdFromUrl,
    performanceProfileIdFromUrl,
    performanceProfileSourceIdFromUrl,
  ]);

  // GET /bots/:id once
  useEffect(() => {
    if (!botId) return;
    if (isAuthLoading) return;
    let isMounted = true;
    setIsLoadingData(true);

    const fetchBotDetails = async () => {
      try {
        setBotDetailsError(null);
        const token = await getBotDetailsApiToken();

        const data = await fetchCurrentBotDetails(token);

        if (isMounted) {
          applyBotDetailsData(data);

          const rawTimeframeParam = requestedTimeframeParam;
          const nextTimeframe = rawTimeframeParam
            ? normalizeTimeframeParam(rawTimeframeParam)
            : getDefaultTimeframeForBot(data);

          setTimeframe((currentTimeframe) =>
            currentTimeframe === nextTimeframe ? currentTimeframe : nextTimeframe,
          );

          if (rawTimeframeParam && rawTimeframeParam !== nextTimeframe) {
            const nextParams = new URLSearchParams(searchParamsString);
            nextParams.set('timeframe', nextTimeframe);
            setSearchParams(nextParams);
          }

          try {
            pushEvent({
              event: BOT_ANALYTICS_EVENTS.detailsViewed,
              botId,
              botName: data.name,
              botMode: data.botMode,
              source: location.pathname,
            });
          } catch (trackingErr) {
            console.error('Tracking error:', trackingErr);
          }
        }
      } catch (err) {
        // Expected private/missing bots render the inline gate panel.
        // Never show a Danger toast here. That toast made a normal login gate
        // look like a product outage.
        console.error('Error fetching bot details:', err);
        if (isMounted) {
          setBotDetailsError(err instanceof Error ? err : new Error(String(err)));
        }
        pushEvent({
          event: BOT_ANALYTICS_EVENTS.detailsError,
          botId,
          error: String(err),
          source: location.pathname,
        });
      } finally {
        if (isMounted) setIsLoadingData(false);
      }
    };

    fetchBotDetails();
    return () => {
      isMounted = false;
    };
  }, [
    botId,
    isAuthenticated,
    isAuthLoading,
    requestedTimeframeParam,
    searchParamsString,
    getBotDetailsApiToken,
    setSearchParams,
    applyBotDetailsData,
    fetchCurrentBotDetails,
    location.pathname,
  ]);

  useEffect(() => {
    const noPerformanceStats =
      !botDetails?.performanceStats || botDetails.performanceStats.length === 0;
    const shouldAutoRefresh = !isLoadingData && isAuthenticated && noPerformanceStats && botDetails;

    if (shouldAutoRefresh) {
      autoRefreshIntervalRef.current = setInterval(async () => {
        try {
          const token = await getBotDetailsApiToken();
          if (token) {
            const data = await fetchCurrentBotDetails(token);
            applyBotDetailsData(data);
            const hasPerformanceStats = (data.performanceStats?.length || 0) > 0;
            if (hasPerformanceStats && autoRefreshIntervalRef.current) {
              clearInterval(autoRefreshIntervalRef.current);
              autoRefreshIntervalRef.current = null;
            }
          }
        } catch (err) {
          console.error('[Auto-refresh] Error:', err);
        }
      }, 30000);
    } else {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    }

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [
    botDetails,
    isLoadingData,
    isAuthenticated,
    getBotDetailsApiToken,
    applyBotDetailsData,
    fetchCurrentBotDetails,
  ]);

  const liveStateDeploymentId = deploymentIdFromUrl || botDetails?.activeDeployment?.id || null;

  useEffect(() => {
    setLatestPortfolioValue(null);
    setLatestCash(null);
    setDeploymentPositions(null);
    setDeploymentPositionsUpdatedAt(null);
    setDeploymentOrders([]);
    setDeploymentOrdersMeta({ lastUpdated: null, source: null });
    setOrdersError('');
  }, [botId, liveStateDeploymentId]);

  const refreshDeploymentLiveState = useCallback(async () => {
    const deploymentId = liveStateDeploymentId;
    if (!deploymentId || !isAuthenticated || !botDetails?.isOwner) {
      setDeploymentPositions(null);
      setDeploymentPositionsUpdatedAt(null);
      setDeploymentOrders([]);
      setDeploymentOrdersMeta({ lastUpdated: null, source: null });
      setOrdersError('');
      return true;
    }

    if (liveStateInFlightRef.current) {
      return false;
    }

    liveStateInFlightRef.current = true;
    setIsLoadingOrders(true);
    setOrdersError('');
    try {
      const token = await getBotDetailsApiToken();
      const data = (await fetchWithAuth(
        token,
        `/deploy/${encodeURIComponent(deploymentId)}/live-state`,
      )) as DeploymentLiveStateResponse;

      const livePositions = data.positions;
      const liveOrders = data.orders;
      const livePositionItems = livePositions?.items;
      const liveOrderItems = liveOrders?.orders;
      setDeploymentSummary((current) => ({
        ...(current || {}),
        id: data.deploymentId || deploymentId,
        botId: current?.botId || botId,
        status: data.status || current?.status || 'unknown',
        schedule: mergeDeploymentSchedule(data.schedule, current?.schedule),
      }));
      applyLatestAccountSnapshot(data.account);
      if (Array.isArray(livePositionItems)) {
        setDeploymentPositions(livePositionItems);
        setDeploymentPositionsUpdatedAt(
          livePositions?.lastUpdated || data.source?.observedAt || data.generatedAt || null,
        );
      }
      if (liveOrders?.telemetryStatus === 'unavailable') {
        setOrdersError('Recent orders are temporarily unavailable.');
      } else {
        setDeploymentOrders(Array.isArray(liveOrderItems) ? liveOrderItems : []);
        setDeploymentOrdersMeta({
          lastUpdated:
            liveOrders?.lastUpdated ||
            liveOrders?.last_updated ||
            data.source?.ordersObservedAt ||
            null,
          source: data.source?.orders || liveOrders?.source || null,
        });
        setOrdersError('');
      }
      liveStateFailureStreakRef.current = 0;
      return true;
    } catch (err) {
      console.warn('[BotDetails] Could not load deployment live state:', err);
      // Keep the last good account/positions snapshot while orders/status retry.
      setOrdersError(err instanceof Error ? err.message : 'Unable to load deployment activity.');
      liveStateFailureStreakRef.current += 1;
      return false;
    } finally {
      liveStateInFlightRef.current = false;
      setIsLoadingOrders(false);
    }
  }, [
    liveStateDeploymentId,
    isAuthenticated,
    botDetails?.isOwner,
    getBotDetailsApiToken,
    applyLatestAccountSnapshot,
    botId,
  ]);

  useEffect(() => {
    if (!liveStateDeploymentId || !isAuthenticated || !botDetails?.isOwner) {
      return undefined;
    }

    let cancelled = false;
    liveStateFailureStreakRef.current = 0;

    const clearLiveStatePoll = () => {
      if (liveStatePollTimeoutRef.current) {
        clearTimeout(liveStatePollTimeoutRef.current);
        liveStatePollTimeoutRef.current = null;
      }
    };

    const scheduleNextLiveStatePoll = (succeeded: boolean) => {
      if (cancelled) return;
      const failureStreak = succeeded ? 0 : liveStateFailureStreakRef.current;
      // Healthy polls stay at 15s. Persistent failures back off to 30s then 60s.
      const delayMs = Math.min(60000, 15000 * 2 ** failureStreak);
      clearLiveStatePoll();
      liveStatePollTimeoutRef.current = setTimeout(async () => {
        const ok = await refreshDeploymentLiveState();
        scheduleNextLiveStatePoll(ok !== false);
      }, delayMs);
    };

    (async () => {
      const ok = await refreshDeploymentLiveState();
      scheduleNextLiveStatePoll(ok !== false);
    })();

    return () => {
      cancelled = true;
      clearLiveStatePoll();
    };
  }, [liveStateDeploymentId, isAuthenticated, botDetails?.isOwner, refreshDeploymentLiveState]);

  const getHistoricalReturns = useCallback(
    async (
      theBot: BotDetailsData,
      freq: TimeframeConfig['frequency'],
      tf: TimeframeKey,
      options: HistoricalReturnsOptions = {},
    ) => {
      if (!theBot) return;
      const requestKey = options.requestKey || '';
      const clearOnEmpty = Boolean(options.clearOnEmpty);
      if (requestKey) {
        chartRequestKeyRef.current = requestKey;
      }
      const isCurrentRequest = () => !requestKey || chartRequestKeyRef.current === requestKey;
      const clearChartIfNeeded = () => {
        if (clearOnEmpty && isCurrentRequest()) {
          setChartData([]);
          setChartDataIsFresh(true);
          setChartMarkers([]);
          setCashFlowAvailability(null);
          setAdjustedReturnPercent(null);
        }
      };
      setIsLoadingChart(true);

      try {
        const endDate = new Date();
        let startDate = new Date();

        switch (tf) {
          case 'returnAll':
            startDate = new Date(getBotPerformanceStartDate(theBot));
            break;
          case 'return24h':
            startDate.setHours(endDate.getHours() - 24);
            break;
          case 'return7d':
            startDate.setDate(endDate.getDate() - 7);
            break;
          case 'return30d':
            startDate.setDate(endDate.getDate() - 30);
            break;
          case 'return3mo':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case 'return6mo':
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case 'return1y':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          case 'return2y':
            startDate.setFullYear(endDate.getFullYear() - 2);
            break;
          default:
            startDate = new Date(getBotPerformanceStartDate(theBot));
        }

        if (Number.isNaN(startDate.getTime())) {
          startDate = new Date('2000-01-01T00:00:00Z');
        }

        const performanceProfileId = getBotPerformanceProfileId(
          theBot,
          performanceProfileIdFromUrl,
        );
        let q = performanceProfileId
          ? `performanceProfileId=${encodeURIComponent(performanceProfileId)}`
          : `botId=${encodeURIComponent(theBot.id)}`;
        if (performanceProfileId && performanceProfileSourceIdFromUrl) {
          q += `&performanceProfileSourceId=${encodeURIComponent(performanceProfileSourceIdFromUrl)}`;
        }
        const startTs = Math.floor(startDate.getTime() / 1000);
        const endTs = Math.floor(endDate.getTime() / 1000);

        const dynamicLimit = getLimitForTimeframe(tf, freq);

        if (!(performanceProfileId && tf === 'returnAll')) {
          q += `&start_timestamp=${startTs}`;
        }
        q += `&end_timestamp=${endTs}&frequency=${freq}&limit=${dynamicLimit}&sort=desc`;

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = await getBotDetailsApiToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const url = `${process.env.REACT_APP_API_URL}/historical-returns?${q}`;
        const resp = await fetch(url, { headers });
        const json = (await resp.json()) as HistoricalReturnsResponse;

        if (!isCurrentRequest()) {
          return;
        }

        if (resp.ok && json.items) {
          const latestEventMs = json.latest_event_timestamp
            ? new Date(json.latest_event_timestamp).getTime()
            : null;
          const freshnessWindowMs = freq === 'hour' ? 2.5 * 60 * 60 * 1000 : 36 * 60 * 60 * 1000;
          const historyIsFresh =
            latestEventMs === null ||
            !Number.isFinite(latestEventMs) ||
            endDate.getTime() - latestEventMs <= freshnessWindowMs;
          setChartDataIsFresh(historyIsFresh);
          setChartMarkers([
            ...(Array.isArray(json.timeline_markers) ? json.timeline_markers : []),
            ...(Array.isArray(json.cash_flow_markers) ? json.cash_flow_markers : []),
          ]);
          setCashFlowAvailability(json.cash_flow_availability || null);
          setAdjustedReturnPercent(
            typeof json.adjusted_return === 'number' && Number.isFinite(json.adjusted_return)
              ? json.adjusted_return * 100
              : null,
          );

          if (json.is_new_bot || json.is_new_profile) {
            clearChartIfNeeded();
            return;
          }

          const portfolioData: ChartPoint[] = json.items
            .map((item) => ({
              date: normalizeChartTimestamp(item.timestamp, freq),
              portfolio_value: item.portfolio_value,
              cash_adjusted_portfolio_value: item.cash_adjusted_portfolio_value,
              cash: item.cash,
            }))
            .sort((a, b) => a.date - b.date);

          const latestPortfolioPoint = portfolioData[portfolioData.length - 1] || null;
          const verifiedCurrentSnapshot = botDetailsRef.current?.latestAccountSnapshot;
          const hasVerifiedCurrentSnapshot = Boolean(
            verifiedCurrentSnapshot?.found &&
            (verifiedCurrentSnapshot.latestPortfolioValue !== null ||
              verifiedCurrentSnapshot.latestCash !== null),
          );
          const hasDeploymentLiveState = Boolean(
            deploymentIdFromUrl || botDetailsRef.current?.activeDeployment?.id,
          );
          if (!hasVerifiedCurrentSnapshot && !hasDeploymentLiveState) {
            applyLatestAccountSnapshot({
              latest_portfolio_value: json.latest_portfolio_value,
              latest_cash: json.latest_cash,
            }) ||
              applyLatestAccountSnapshot({
                latest_portfolio_value: latestPortfolioPoint?.portfolio_value ?? null,
                latest_cash: latestPortfolioPoint?.cash ?? null,
              });
          }

          if (portfolioData.length > 0) {
            setHasEverHadData(true);
            let merged = portfolioData;
            const earliestPortDate = portfolioData[0].date;
            const latestPortDate = portfolioData[portfolioData.length - 1].date;

            try {
              const rawBench = await fetchBenchmarkData(
                '^GSPC',
                new Date(earliestPortDate),
                new Date(latestPortDate),
                freq,
              );
              const benchNormalized = rawBench.map((b) => ({
                date: normalizeChartTimestamp(b.date, freq),
                spy_benchmark: b.spy_benchmark,
              }));
              const benchFilled = fillBenchmarkEdges(portfolioData, benchNormalized);
              const benchScaled = scaleBenchmark(portfolioData, benchFilled);
              merged = mergeChartData(portfolioData, benchScaled);
            } catch (benchmarkErr) {
              console.warn('[BotDetails] Could not load benchmark data:', benchmarkErr);
            }

            if (isCurrentRequest()) {
              setChartData(merged);
            }
          } else {
            clearChartIfNeeded();
          }
        } else {
          clearChartIfNeeded();
        }
      } catch (err) {
        console.error('Error fetching historical returns:', err);
        if (isCurrentRequest()) {
          setToastMessage('Error fetching historical returns. Please try again later.');
          setToastVariant('danger');
          setShowToast(true);
          clearChartIfNeeded();
        }
      } finally {
        if (isCurrentRequest()) {
          setIsLoadingChart(false);
        }
      }
    },
    [
      getBotDetailsApiToken,
      applyLatestAccountSnapshot,
      deploymentIdFromUrl,
      performanceProfileIdFromUrl,
      performanceProfileSourceIdFromUrl,
    ],
  );

  useEffect(() => {
    if (!botDetails) {
      setBotStats(undefined);
      return;
    }
    const psObj = botDetails.performanceStats?.find((ps) => ps.timeframe === timeframe);
    setBotStats(psObj);
  }, [botDetails, timeframe]);

  // Fetch chart data for the selected performance source. Keep existing chart data during polling refreshes.
  useEffect(() => {
    const currentBot = botDetailsRef.current;
    if (!currentBot || !chartSourceKey) return;
    if (!timeframe) return;

    if (timeframesMap[timeframe]) {
      const freq = timeframesMap[timeframe]?.frequency || 'day';
      const requestKey = `${chartSourceKey}|${timeframe}`;
      const clearOnEmpty = chartRequestKeyRef.current !== requestKey;
      getHistoricalReturns(currentBot, freq, timeframe, { requestKey, clearOnEmpty });
    } else {
      chartRequestKeyRef.current = '';
      setChartData([]);
      setChartMarkers([]);
      setCashFlowAvailability(null);
      setAdjustedReturnPercent(null);
      setIsLoadingChart(false);
    }
  }, [chartSourceKey, timeframe, timeframesMap, getHistoricalReturns]);

  const confirmStopBot = async () => {
    try {
      setIsStoppingBot(true);
      const token = await getBotDetailsApiToken();
      if (!botDetails) return;
      const deploymentIdToStop = currentDeployment?.id || deploymentIdFromUrl;

      if (!deploymentIdToStop) {
        setToastMessage('This bot does not have an active deployment to stop.');
        setToastVariant('warning');
        setShowToast(true);
        return;
      }

      const stopUrl = `${process.env.REACT_APP_API_URL}/deploy/stop`;
      const resp = await fetch(stopUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deploy_id: deploymentIdToStop }),
      });

      if (resp.ok) {
        setShowStopConfirm(false);
        setSaved(true);
        setDeploymentSummary((current) => ({
          ...(current || {}),
          id: deploymentIdToStop,
          botId: current?.botId || botId,
          status: 'stopping',
        }));
        try {
          const latestDetails = await fetchCurrentBotDetails(token);
          applyBotDetailsData(latestDetails);
        } catch (refreshErr) {
          console.warn('Stopped bot but could not immediately refresh details:', refreshErr);
        }
        setTimeout(() => setSaved(false), 2000);
        pushEvent({
          event: BOT_ANALYTICS_EVENTS.stopRequested,
          botId,
          botName: botDetails.name,
          deploymentId: deploymentIdToStop,
          source: location.pathname,
        });
      } else {
        setToastMessage(`Error while stopping bot: ${resp.statusText}`);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (err) {
      console.error('Error stopping bot:', err);
      setToastMessage('Error stopping bot. Please try again later.');
      setToastVariant('danger');
      setShowToast(true);
      pushEvent({
        event: BOT_ANALYTICS_EVENTS.stopError,
        botId,
        error: String(err),
        source: location.pathname,
      });
    } finally {
      setIsStoppingBot(false);
    }
  };

  const onStopBot = () => {
    setAcceptedStopPositionAcknowledgement(false);
    setShowStopConfirm(true);
  };

  const closeStopConfirm = () => {
    setShowStopConfirm(false);
    setAcceptedStopPositionAcknowledgement(false);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setToastMessage('API Key copied to clipboard!');
        setToastVariant('success');
        setShowToast(true);
      },
      () => {
        setToastMessage('Failed to copy API Key. Please copy manually.');
        setToastVariant('danger');
        setShowToast(true);
      },
    );
  };

  const handleEditNameClick = () => {
    if (!botDetails) return;
    setEditingName(true);
    setNewName(botDetails.name ?? '');
  };

  const handleCancelEdit = () => {
    setEditingName(false);
    setNewName('');
  };

  const handleEditRealPaperClick = () => {
    if (!botDetails) return;
    setEditingRealPaper(true);
    setNewRealPaper(botDetails.isRealMoney ?? false);
  };

  const handleCancelRealPaperClick = () => {
    setEditingRealPaper(false);
    setNewRealPaper(botDetails?.isRealMoney ?? false);
  };

  const handleSaveName = async () => {
    if (!botDetails) return;
    try {
      const token = await getBotDetailsApiToken();
      const updateUrl = `${process.env.REACT_APP_API_URL}/bots/${encodeURIComponent(botDetails.id)}`;
      const resp = await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName }),
      });
      if (!resp.ok) {
        throw new Error(`Failed to update bot name. Status: ${resp.status}`);
      }
      const updated = (await resp.json()) as Partial<BotDetailsData>;

      setBotDetails((prevDetails) =>
        prevDetails ? { ...prevDetails, name: updated.name || prevDetails.name } : prevDetails,
      );
      setEditingName(false);
      setToastMessage('Bot name updated!');
      setToastVariant('success');
      setShowToast(true);
    } catch (err) {
      console.error('Error updating bot name:', err);
      setToastMessage('Error updating bot name. Please try again.');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleSaveRealPaper = async () => {
    if (!botDetails) return;
    try {
      const token = await getBotDetailsApiToken();
      const updateUrl = `${process.env.REACT_APP_API_URL}/bots/${encodeURIComponent(botDetails.id)}`;
      const resp = await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isRealMoney: newRealPaper }),
      });
      if (!resp.ok) {
        throw new Error(`Failed to update bot real/paper status. Status: ${resp.status}`);
      }
      const updated = (await resp.json()) as Partial<BotDetailsData>;

      setBotDetails((prevDetails) =>
        prevDetails
          ? { ...prevDetails, isRealMoney: updated.isRealMoney ?? prevDetails.isRealMoney }
          : prevDetails,
      );

      setEditingRealPaper(false);
      setToastMessage('Bot real/paper status updated!');
      setToastVariant('success');
      setShowToast(true);
    } catch (err) {
      console.error('Error updating bot real/paper status:', err);
      setToastMessage('Error updating bot real/paper status. Please try again.');
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  let trendDirection: 'neutral' | 'up' | 'down' = 'neutral';
  if (chartData.length > 1) {
    const firstVal = chartData[0]?.portfolio_value;
    const lastVal = chartData[chartData.length - 1]?.portfolio_value;
    if (typeof firstVal === 'number' && typeof lastVal === 'number') {
      if (lastVal > firstVal) trendDirection = 'up';
      else if (lastVal < firstVal) trendDirection = 'down';
    }
  }

  // Update isOwner check to ensure user is authenticated first and directly use bot details
  // The isOwner property should come directly from the API
  const isOwner = Boolean(botDetails?.isOwner);
  const detailDeployment = botDetails?.activeDeployment || null;
  const currentDeployment = deploymentSummary
    ? {
        ...(detailDeployment || {}),
        ...deploymentSummary,
        brokerCredential:
          deploymentSummary.brokerCredential || detailDeployment?.brokerCredential || null,
        revisionVersion:
          deploymentSummary.revisionVersion ?? detailDeployment?.revisionVersion ?? null,
        deploymentMode:
          deploymentSummary.deploymentMode ?? detailDeployment?.deploymentMode ?? null,
        schedule: mergeDeploymentSchedule(deploymentSummary.schedule, detailDeployment?.schedule),
      }
    : detailDeployment;
  const activeBrokerCredential = currentDeployment?.brokerCredential || null;
  const activeBrokerLabel = formatBrokerConnectionLabel(activeBrokerCredential);
  const deploymentStatus = (currentDeployment?.status || '').toLowerCase();
  const isDeploymentRunning = DEPLOYMENT_RUNNING_STATUSES.includes(deploymentStatus);
  const deploymentStatusLabel = deploymentStatus
    ? formatDeploymentSummaryStatus(deploymentStatus, currentDeployment?.schedule)
    : 'Not deployed';
  const deploymentErrorDetails = String(currentDeployment?.errorDetails || '').toLowerCase();
  const isScheduledWaiting = deploymentStatus === 'scheduled';
  const requestedDeploymentMode = String(
    currentDeployment?.deploymentMode || currentDeployment?.schedule?.deploymentMode || '',
  ).toLowerCase();
  // Always On bots still receive last-run, cadence, and EventBridge leftovers from
  // Bot Manager. Those fields must not switch the stopped banner to scheduled copy.
  const deploymentHasScheduledMetadata =
    requestedDeploymentMode !== 'always_on' &&
    (isScheduledWaiting ||
      requestedDeploymentMode === 'scheduled' ||
      Boolean(
        currentDeployment?.schedule?.scheduleStatus ||
        currentDeployment?.schedule?.scheduleBackend ||
        currentDeployment?.schedule?.cadence,
      ));
  const isDeploymentPending =
    DEPLOYMENT_PENDING_STATUSES.includes(deploymentStatus) && !isScheduledWaiting;
  const scheduledHealth = String(currentDeployment?.schedule?.health || '').toLowerCase();
  const scheduledIsStopped =
    deploymentHasScheduledMetadata && isDeploymentScheduleStopped(currentDeployment?.schedule);
  const scheduledNeedsAttention =
    deploymentHasScheduledMetadata && scheduledHealth === 'degraded' && !scheduledIsStopped;
  const scheduleHealthCategory = String(
    currentDeployment?.schedule?.healthCategory || '',
  ).toLowerCase();
  const scheduleFailureReason =
    currentDeployment?.schedule?.lastRunErrorDetails ||
    currentDeployment?.schedule?.lastRunError ||
    currentDeployment?.schedule?.scheduleLaunchFailureReason ||
    currentDeployment?.schedule?.scheduleTaskStoppedReason ||
    currentDeployment?.errorDetails ||
    null;
  const isDeploymentCompleted = DEPLOYMENT_COMPLETED_STATUSES.includes(deploymentStatus);
  const isDeploymentCompletedWithErrors =
    DEPLOYMENT_COMPLETED_WITH_ERRORS_STATUSES.includes(deploymentStatus);
  const isDeploymentTerminalCompletion = isDeploymentCompleted || isDeploymentCompletedWithErrors;
  const isDeploymentStopFailed = DEPLOYMENT_STOP_FAILED_STATUSES.includes(deploymentStatus);
  const isDeploymentStopping = DEPLOYMENT_STOPPING_STATUSES.includes(deploymentStatus);
  const isDeploymentStopped =
    scheduledIsStopped ||
    DEPLOYMENT_STOPPED_STATUSES.includes(deploymentStatus) ||
    isDeploymentTerminalCompletion ||
    deploymentErrorDetails.includes('bot_deleted');

  useEffect(() => {
    if (!showStopConfirm || !isDeploymentStopped) return;

    setShowStopConfirm(false);
    setAcceptedStopPositionAcknowledgement(false);
    setIsStoppingBot(false);
  }, [showStopConfirm, isDeploymentStopped]);

  const isDeploymentStopState =
    isDeploymentStopping || isDeploymentStopped || isDeploymentStopFailed;
  const isDeploymentFailed =
    !isDeploymentStopFailed &&
    !isDeploymentCompletedWithErrors &&
    (deploymentStatus.includes('failed') || deploymentStatus === 'not_found');
  const botHeaderDeploymentStatusLabel = isDeploymentStopped ? 'Stopped' : deploymentStatusLabel;
  const botHeaderDeploymentStatusClass = isDeploymentStopped ? 'stopped' : deploymentStatus;
  const revisionVersion = currentDeployment?.revisionVersion ?? null;
  const aiStrategyId = currentDeployment?.strategy?.aiStrategyId || null;
  const aiStrategyRevisionId = currentDeployment?.aiStrategyRevisionId || null;
  const botStrategyId =
    botDetails?.strategy?.id ||
    botDetails?.strategyId ||
    botDetails?.strategy_id ||
    currentDeployment?.strategy?.id ||
    currentDeployment?.strategyId ||
    currentDeployment?.strategy_id ||
    null;

  const apiKeyToShow = botDetails?.apiKey || fallbackApiKey;
  const noPerformanceStats =
    !botDetails?.performanceStats || botDetails.performanceStats.length === 0;
  const botPositionsSource = botDetails?.positions;
  const botPositions = liveStateDeploymentId
    ? deploymentPositions || []
    : Array.isArray(botPositionsSource)
      ? botPositionsSource
      : [];
  const hasAccountSnapshot = latestPortfolioValue !== null || latestCash !== null;
  const initialNoDataScenario =
    Boolean(botDetails) &&
    !isLoadingData &&
    !deploymentHasScheduledMetadata &&
    !scheduledNeedsAttention &&
    !isDeploymentPending &&
    !isDeploymentStopState &&
    !isDeploymentFailed &&
    !hasEverHadData &&
    noPerformanceStats &&
    !hasAccountSnapshot;
  const scheduledAwaitingAccountData =
    Boolean(botDetails) &&
    !isLoadingData &&
    deploymentHasScheduledMetadata &&
    !scheduledNeedsAttention &&
    !hasAccountSnapshot &&
    noPerformanceStats;
  const awaitingInitialAccountData =
    Boolean(botDetails) &&
    !isLoadingData &&
    isDeploymentRunning &&
    !deploymentHasScheduledMetadata &&
    !hasAccountSnapshot;
  const deploymentUpdatedAt =
    currentDeployment?.updatedAt ||
    currentDeployment?.updated_at ||
    currentDeployment?.createdAt ||
    currentDeployment?.created_at ||
    null;
  const firstAccountUpdateWaitMinutes = deploymentUpdatedAt
    ? moment().diff(moment(deploymentUpdatedAt), 'minutes')
    : 0;
  const firstAccountUpdateDelayed =
    awaitingInitialAccountData && firstAccountUpdateWaitMinutes >= 10;
  const hideAccountSurfaces = isDeploymentStopping || isDeploymentStopped;
  const showPerformanceSection = Boolean(botDetails) && !hideAccountSurfaces;
  const showActivitySection = showPerformanceSection;
  const displayState = isDeploymentStopFailed
    ? 'stop_failed'
    : isDeploymentStopping
      ? 'stopping'
      : isDeploymentStopped && deploymentHasScheduledMetadata
        ? 'scheduled_stopped'
        : isDeploymentStopped
          ? 'always_on_stopped'
          : isDeploymentFailed || isDeploymentCompletedWithErrors
            ? 'failed'
            : scheduledNeedsAttention || firstAccountUpdateDelayed
              ? 'degraded'
              : isDeploymentPending
                ? 'starting'
                : initialNoDataScenario ||
                    scheduledAwaitingAccountData ||
                    awaitingInitialAccountData
                  ? 'awaiting_data'
                  : 'healthy';
  const operationalBanner: BotStatusPanelProps | null = (() => {
    switch (displayState) {
      case 'stop_failed':
        return {
          variant: 'failed',
          title: 'Bot stop needs attention',
          body:
            currentDeployment?.errorDetails ||
            'BotSpot could not finish stopping this bot. Open Logs for details.',
          icon: faTimesCircle,
        };
      case 'stopping':
        return {
          variant: 'stopping',
          title: 'Stopping bot',
          body: 'BotSpot is finishing the shutdown.',
          icon: faRefresh,
          spin: true,
        };
      case 'scheduled_stopped':
        return {
          variant: 'failed',
          title: 'This bot is stopped',
          body: 'Automated trading has ended for this deployment.',
          icon: faTimesCircle,
        };
      case 'always_on_stopped':
        return {
          variant: 'failed',
          title: 'This bot is stopped',
          body: 'Automated trading has ended for this deployment.',
          icon: faTimesCircle,
        };
      case 'failed':
        // A bot that failed at start never became a running bot, so it has no
        // schedule health and used to fall through to a dead-end banner. The
        // owner needs the same Fix with AI route as a bot that crashed mid-run.
        return {
          variant: 'failed',
          title: 'This bot needs attention',
          body: currentDeployment?.errorDetails || 'Open Logs for the latest runtime details.',
          icon: faTimesCircle,
          action: {
            label: 'Fix with AI',
            kind: 'ai',
            onClick: () => handleScheduleAi(),
          },
        };
      case 'degraded':
        if (!firstAccountUpdateDelayed && scheduleHealthCategory === 'broker_auth_required') {
          return {
            variant: 'waiting',
            title: 'Reconnect your broker',
            body: 'This schedule cannot access the connected broker until you reconnect it.',
            icon: faClock,
            action: activeBrokerCredential?.id
              ? {
                  label: `Reconnect ${activeBrokerCredential.broker || 'broker'}`,
                  href: `/brokerage-accounts/${encodeURIComponent(activeBrokerCredential.id)}`,
                }
              : undefined,
          };
        }
        if (!firstAccountUpdateDelayed && scheduleHealthCategory === 'run_failure') {
          return {
            variant: 'waiting',
            title: 'Latest scheduled run crashed',
            body: scheduleFailureReason || 'The strategy stopped with a code error.',
            icon: faClock,
            action: {
              label: 'Fix with AI',
              kind: 'ai',
              onClick: () => handleScheduleAi(),
            },
          };
        }
        return {
          variant: 'waiting',
          title: firstAccountUpdateDelayed
            ? 'Broker update is delayed'
            : scheduleHealthCategory === 'schedule_launch_failure'
              ? 'Bot could not start'
              : scheduleHealthCategory === 'overdue'
                ? 'Bot run is delayed'
                : 'This bot needs attention',
          body: firstAccountUpdateDelayed
            ? 'Existing account history remains available while BotSpot waits for a fresh broker snapshot.'
            : scheduleFailureReason ||
              'Existing account history remains available. Open Logs for details.',
          icon: faClock,
          action: firstAccountUpdateDelayed
            ? undefined
            : {
                label: 'Fix with AI',
                kind: 'ai',
                onClick: () => handleScheduleAi(),
              },
        };
      case 'starting':
        return {
          variant: 'deploying',
          title: 'Starting this bot',
          body: 'BotSpot is connecting the broker and preparing the runtime.',
          icon: faRefresh,
          spin: true,
        };
      case 'awaiting_data':
        return {
          variant: 'waiting',
          title: 'Account data is being collected',
          body: 'Metrics will populate after the first verified broker snapshot.',
          icon: faClock,
        };
      default:
        return null;
    }
  })();

  useEffect(() => {
    if (!isOwner || !botStrategyId || !isAuthenticated) {
      setMarketplaceListing(null);
      setIsLoadingMarketplaceListing(false);
      return undefined;
    }

    let cancelled = false;

    const fetchMarketplaceListing = async () => {
      setIsLoadingMarketplaceListing(true);
      try {
        const token = await getBotDetailsApiToken();
        const data = (await fetchWithAuth(
          token,
          `/marketplace/check-published/${encodeURIComponent(botStrategyId)}`,
        )) as MarketplaceCheckResponse;

        if (cancelled) return;

        if (data?.isPublished && data?.listingId) {
          setMarketplaceListing({ id: data.listingId });
        } else {
          setMarketplaceListing(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[BotDetails] Could not load Marketplace listing status:', err);
          setMarketplaceListing(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMarketplaceListing(false);
        }
      }
    };

    fetchMarketplaceListing();

    return () => {
      cancelled = true;
    };
  }, [isOwner, botStrategyId, isAuthenticated, getBotDetailsApiToken]);

  const portfolioValueChange = useMemo(() => {
    if (!chartDataIsFresh) return null;
    return calculatePortfolioValueChange(chartData, latestPortfolioValue);
  }, [chartData, chartDataIsFresh, latestPortfolioValue]);

  const cashFlowAdjustmentUnavailable = Boolean(
    cashFlowAvailability && cashFlowAvailability.status !== 'available',
  );
  const chartReturnPercent = cashFlowAdjustmentUnavailable
    ? null
    : (adjustedReturnPercent ?? portfolioValueChange?.percent ?? null);

  const displayedBotStats = useMemo(() => {
    if (!botStats?.stats || chartReturnPercent === null) return botStats;
    return {
      ...botStats,
      stats: {
        ...botStats.stats,
        return: chartReturnPercent,
        total_return: chartReturnPercent,
      },
    };
  }, [botStats, chartReturnPercent]);
  const displayedReturnPercent = cashFlowAdjustmentUnavailable
    ? null
    : (chartReturnPercent ?? getStatsReturnPercent(displayedBotStats));
  const returnTone = getSignedMetricTone(displayedReturnPercent);
  const returnValueClass = getSignedMetricToneClass(returnTone);
  const portfolioChangeTone = getSignedMetricTone(portfolioValueChange?.amount ?? null);
  const portfolioChangeClass = getSignedMetricToneClass(portfolioChangeTone);
  const chartEmptyTitle = 'No performance data yet.';
  const chartEmptyDescription =
    'Try another timeframe or check again after the next account update.';

  const handleAskAi = () => {
    navigateToAiAgent(
      navigate,
      {
        prompt: '',
        aiStrategyId,
        revisionId: aiStrategyRevisionId,
        source: 'bot-details',
        context: {
          botId: botDetails?.id,
          botName: botDetails?.name,
          deploymentId: currentDeployment?.id || null,
          deploymentStatus: currentDeployment?.status || null,
        },
      },
      { replace: false },
    );
  };

  const handleScheduleAi = () => {
    const deploymentId = currentDeployment?.id || null;
    const runId =
      currentDeployment?.schedule?.lastRunId ||
      currentDeployment?.schedule?.scheduleLaunchRunId ||
      null;
    const repairHandoff = buildDeploymentFixWithAiHandoff({
      botId: botDetails?.id,
      botName: botDetails?.name,
      deploymentId,
      runId,
      status: currentDeployment?.status || null,
      healthCategory: currentDeployment?.schedule?.healthCategory || null,
      healthReason: currentDeployment?.schedule?.healthReason || null,
      // A start-time rejection has no schedule run to point at. The deployment
      // error is the only description of what went wrong, so the agent needs it.
      error: scheduleFailureReason || currentDeployment?.errorDetails || null,
    });
    navigateToAiAgent(
      navigate,
      {
        prompt: repairHandoff.prompt,
        aiStrategyId,
        revisionId: aiStrategyRevisionId,
        source: repairHandoff.source,
        context: repairHandoff.context,
      },
      { replace: false },
    );
  };

  const getBackingStrategyRecord = (): StrategyRecord | null => {
    if (!aiStrategyId) return null;
    return {
      id: botStrategyId || undefined,
      aiStrategyId,
      strategyId: botStrategyId,
      name:
        currentDeployment?.strategy?.name ||
        botDetails?.strategy?.name ||
        botDetails?.name ||
        'Untitled Strategy',
      marketplace: {
        isInMarketplace: Boolean(marketplaceListing?.id),
        marketplaceListingId: marketplaceListing?.id || null,
      },
    };
  };

  const openBackingStrategyShare = () => {
    const strategy = getBackingStrategyRecord();
    if (!strategy) {
      setToastMessage('This bot does not have a shareable backing strategy.');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }

    openShareStrategy(strategy);
  };

  const selectChartTimeframe = (nextTimeframe: TimeframeKey) => {
    const nextParams = new URLSearchParams(searchParamsString);
    nextParams.set('timeframe', nextTimeframe);
    setSearchParams(nextParams);
    setTimeframe(nextTimeframe);
    pushEvent({
      event: BOT_ANALYTICS_EVENTS.chartPeriodChanged,
      botId,
      period: nextTimeframe,
      source: location.pathname,
    });
  };

  const renderOwnerDiagnostics = () => {
    if (!isOwner || !currentDeployment?.id) return null;

    return (
      <Row className="bot-diagnostics-row bot-owner-panel-row mt-3 fade-in-delayed">
        <Col>
          <Card className="bot-owner-panel-card bot-diagnostics-card">
            <Card.Body>
              <button
                type="button"
                className="bot-owner-panel-toggle bot-logs-toggle"
                onClick={() => setLogsExpanded((expanded) => !expanded)}
                aria-expanded={logsExpanded}
              >
                <span>
                  <span className="bot-owner-panel-title">Logs</span>
                  <span className="bot-owner-panel-subtitle">
                    View startup messages and trade activity
                  </span>
                </span>
                <FontAwesomeIcon icon={logsExpanded ? faChevronUp : faChevronDown} />
              </button>

              {logsExpanded && (
                <div className="bot-owner-panel-content bot-logs-content">
                  <BotDeployLogs
                    deployId={currentDeployment.id}
                    getApiToken={getBotDetailsApiToken}
                    expanded={logsExpanded}
                    onToggle={() => setLogsExpanded((expanded) => !expanded)}
                    hideToggleButton
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderBotStatusPanel = ({
    variant = 'info',
    title,
    body,
    icon = faRefresh,
    spin = false,
    details = [],
    action,
  }: BotStatusPanelProps) => (
    <div className={`bot-state-panel bot-state-panel-${variant} slide-in-up`}>
      <div className="bot-state-panel-icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} spin={spin} />
      </div>
      <div className="bot-state-panel-content">
        <h3>{title}</h3>
        <p>{body}</p>
        {details.length > 0 && (
          <div className="bot-state-panel-details">
            {details.map((detail) => (
              <div key={detail} className="bot-state-panel-detail">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {action && (
        <div className="bot-state-panel-actions">
          {action.href ? (
            <Link
              className={`btn btn-primary btn-sm${
                action.kind === 'ai' ? ' bot-state-panel-action-ai' : ''
              }`}
              to={action.href}
            >
              {action.label}
            </Link>
          ) : (
            <Button
              className={action.kind === 'ai' ? 'bot-state-panel-action-ai' : undefined}
              size="sm"
              variant="primary"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const renderEditableBotMetadata = () => {
    if (!isOwner || !botDetails) return null;

    return (
      <div className="bot-settings-list">
        <div className="bot-settings-row">
          <div>
            <div className="bot-settings-label">Name</div>
            <div className="bot-settings-value">{botDetails.name}</div>
          </div>
          {editingName ? (
            <div className="bot-settings-inline-form">
              <input
                type="text"
                className="form-control"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button
                className="bot-settings-action-button"
                variant="success"
                size="sm"
                onClick={handleSaveName}
              >
                Save
              </Button>
              <Button
                className="bot-settings-action-button"
                variant="outline-light"
                size="sm"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              className="bot-settings-action-button"
              variant="outline-light"
              size="sm"
              onClick={handleEditNameClick}
            >
              Edit
            </Button>
          )}
        </div>

        <div className="bot-settings-row">
          <div>
            <div className="bot-settings-label">Trading mode</div>
            <div className="bot-settings-value">
              {botDetails.isRealMoney ? 'Real Money' : 'Paper'}
            </div>
          </div>
          {editingRealPaper ? (
            <div className="bot-settings-inline-form">
              <select
                className="form-control"
                value={newRealPaper ? 'true' : 'false'}
                onChange={(e) => setNewRealPaper(e.target.value === 'true')}
              >
                <option value="true">Real Money</option>
                <option value="false">Paper Trading</option>
              </select>
              <Button
                className="bot-settings-action-button"
                variant="success"
                size="sm"
                onClick={handleSaveRealPaper}
              >
                Save
              </Button>
              <Button
                className="bot-settings-action-button"
                variant="outline-light"
                size="sm"
                onClick={handleCancelRealPaperClick}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              className="bot-settings-action-button"
              variant="outline-light"
              size="sm"
              onClick={handleEditRealPaperClick}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderOwnerSettings = () => {
    if (!isOwner) return null;

    const settingsSubtitle =
      isLoadingMarketplaceListing || marketplaceListing?.id
        ? 'Name, Marketplace, trading mode, and API key'
        : 'Name, trading mode, and API key';

    return (
      <Row className="settings-zone bot-owner-panel-row fade-in-delayed">
        <Col>
          <Card className="bot-owner-panel-card settings-zone-card bot-owner-settings-card">
            <Card.Body>
              <button
                type="button"
                className="bot-owner-panel-toggle bot-settings-toggle"
                onClick={() => setSettingsExpanded((expanded) => !expanded)}
                aria-expanded={settingsExpanded}
              >
                <span>
                  <span className="bot-owner-panel-title bot-settings-title">Settings</span>
                  <span className="bot-owner-panel-subtitle bot-settings-subtitle">
                    {settingsSubtitle}
                  </span>
                </span>
                <FontAwesomeIcon icon={settingsExpanded ? faChevronUp : faChevronDown} />
              </button>

              {settingsExpanded && (
                <div className="bot-owner-panel-content bot-settings-content">
                  {renderEditableBotMetadata()}

                  {(isLoadingMarketplaceListing || marketplaceListing?.id) && (
                    <div className="bot-settings-row">
                      <div>
                        <div className="bot-settings-label">Marketplace</div>
                        <div className="bot-settings-value">
                          {isLoadingMarketplaceListing ? 'Checking listing...' : 'Listed publicly'}
                        </div>
                      </div>
                      {marketplaceListing?.id && (
                        <Link
                          to={`/marketplace/strategy/${encodeURIComponent(marketplaceListing.id)}/settings`}
                          className="bot-settings-action-button bot-settings-link-button btn btn-sm btn-outline-light"
                        >
                          Marketplace settings
                        </Link>
                      )}
                    </div>
                  )}

                  <div className="bot-settings-row">
                    <div>
                      <div className="bot-settings-label">API Key</div>
                      <div className="bot-settings-value bot-settings-api-value">
                        {showApiKey && apiKeyToShow
                          ? apiKeyToShow
                          : apiKeyToShow
                            ? '****-****-****'
                            : 'Not available'}
                      </div>
                    </div>
                    <div className="bot-settings-inline-form">
                      {showApiKey && apiKeyToShow && (
                        <Button
                          className="bot-settings-action-button"
                          variant="outline-light"
                          size="sm"
                          onClick={() => copyToClipboard(apiKeyToShow)}
                        >
                          <FontAwesomeIcon icon={faCopy} /> Copy
                        </Button>
                      )}
                      <Button
                        className="bot-settings-action-button"
                        variant="outline-light"
                        size="sm"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {!showApiKey ? 'Show' : 'Hide'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderDangerZone = () => {
    if (!isOwner) return null;

    return (
      <Row className="danger-zone mt-4 fade-in-delayed">
        <Col>
          <Card className="danger-zone-card mb-3">
            <Card.Body>
              <div className="danger-zone-content">
                <div>
                  <Card.Title className="mb-1">Danger Zone</Card.Title>
                  <Card.Text className="mb-0">Stop future automated trading for this bot</Card.Text>
                </div>
                {isDeploymentStopped ? (
                  <Button variant="danger" size="sm" disabled>
                    <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                    Stopped
                  </Button>
                ) : saved || isDeploymentStopping ? (
                  <Button variant="warning" size="sm" disabled>
                    <FontAwesomeIcon
                      icon={faRefresh}
                      spin={isDeploymentStopping}
                      className="me-1"
                    />
                    Stopping
                  </Button>
                ) : (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={onStopBot}
                    className="stop-bot-btn"
                  >
                    Stop Bot
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <PageShell className="bot-details-page fade-in" innerClassName="bot-details-shell">
      {botDetails && (
        <Helmet>
          <title>{`${botDetails.name || 'Bot'} | AI Trading Bot on BotSpot`}</title>
          <meta
            name="description"
            content={(
              botDetails.description || 'Explore AI trading bot performance, stats, and more.'
            ).slice(0, 200)}
          />
          <link rel="canonical" href={`https://botspot.trade/bot/${botId}`} />
          <meta property="og:type" content="website" />
          <meta
            property="og:title"
            content={`${botDetails.name || 'Bot'} | AI Trading Bot on BotSpot`}
          />
          <meta
            property="og:description"
            content={(
              botDetails.description || 'Explore AI trading bot performance, stats, and more.'
            ).slice(0, 200)}
          />
          <meta property="og:image" content={`https://botspot.trade/social-share-image.jpg`} />
          <meta property="og:url" content={`https://botspot.trade/bot/${botId}`} />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
      )}
      {isLoadingData && (
        <div className="loading-fullscreen">
          <Spinner animation="border" variant="light" />
          <div className="loading-text">Loading Bot Details...</div>
        </div>
      )}

      <Modal
        show={showStopConfirm}
        onHide={() => !isStoppingBot && closeStopConfirm()}
        centered
        dialogClassName="bot-stop-modal"
      >
        <Modal.Header closeButton={!isStoppingBot}>
          <Modal.Title>Stop this bot?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            BotSpot will stop the active run for <strong>{botDetails?.name || 'this bot'}</strong>.
            The bot will stay visible here with its stopped status, logs, settings, and history.
          </p>
          {activeBrokerLabel && (
            <div className="bot-stop-modal-context">
              Active connection: <strong>{activeBrokerLabel}</strong>
            </div>
          )}
          <label
            className="bot-stop-modal-acknowledgement"
            htmlFor="stop-bot-position-acknowledgement"
          >
            <input
              id="stop-bot-position-acknowledgement"
              type="checkbox"
              checked={acceptedStopPositionAcknowledgement}
              disabled={isStoppingBot}
              onChange={(event) => setAcceptedStopPositionAcknowledgement(event.target.checked)}
            />
            <span>{STOP_BOT_POSITION_ACKNOWLEDGEMENT}</span>
          </label>
          {!acceptedStopPositionAcknowledgement && (
            <div className="bot-stop-modal-helper">Confirm this before stopping the bot.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" onClick={closeStopConfirm} disabled={isStoppingBot}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmStopBot}
            disabled={isStoppingBot || !acceptedStopPositionAcknowledgement}
          >
            {isStoppingBot ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Stopping...
              </>
            ) : (
              'Stop Bot'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {!isLoadingData && !botDetails && botDetailsError && (
        <Container className="fade-in-delayed">
          {(() => {
            const errorCode =
              botDetailsError && typeof botDetailsError === 'object'
                ? (botDetailsError as Error & { code?: string }).code
                : undefined;
            const showLoginGate = !isAuthenticated && errorCode !== 'bot_not_found';
            return (
              <div
                className={`bot-details-error-panel bot-details-error-panel--gate${
                  showLoginGate ? ' bot-details-error-panel--login' : ''
                }`}
              >
                {showLoginGate ? (
                  <>
                    <h3>Log in to view this bot</h3>
                    <p>
                      This bot is private to its owner. If this is your bot, log in and BotSpot will
                      bring you back here.
                    </p>
                    <a className="bot-details-error-panel__cta" href={botDetailsLoginHref}>
                      Log in
                    </a>
                  </>
                ) : (
                  <>
                    <h3>Couldn&apos;t load this bot</h3>
                    <p>
                      This may be an old bot, a stopped bot, or a bot your account cannot access.
                    </p>
                    <a
                      className="bot-details-error-panel__cta"
                      href={isAuthenticated ? '/my-bots' : botDetailsLoginHref}
                    >
                      {isAuthenticated ? 'Open my bots' : 'Log in'}
                    </a>
                  </>
                )}
              </div>
            );
          })()}
        </Container>
      )}

      {!isLoadingData && botDetails && (
        <Container className="fade-in-delayed">
          <div className="bot-header-section slide-in-up">
            <div className="bot-header-content">
              <div className="bot-header-main">
                <div className="bot-header-copy">
                  <h1 className="bot-title">{botDetails.name}</h1>
                  <StrategyAutomationBadge
                    aiEnabled={botDetails.aiEnabled ?? botDetails.activeDeployment?.aiEnabled}
                  />

                  <h4 className="bot-owner">
                    by&nbsp;
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-profile">
                          Click to see this user's public profile
                        </Tooltip>
                      }
                    >
                      <Link className="link" to={`/profile/${botDetails?.owner?.id}`}>
                        {botDetails?.owner?.nickname || 'Anonymous'}
                      </Link>
                    </OverlayTrigger>
                  </h4>

                  <div className="bot-meta-row">
                    {deploymentStatus && (
                      <span
                        className={`bot-meta-item bot-meta-status status-${botHeaderDeploymentStatusClass}`}
                      >
                        <span className="bot-meta-dot" />
                        {botHeaderDeploymentStatusLabel}
                      </span>
                    )}
                    {revisionVersion !== null && revisionVersion !== undefined && (
                      <span className="bot-meta-item">Version {revisionVersion}</span>
                    )}
                    <span className="bot-meta-item">
                      {botDetails.isRealMoney ? 'Real Money' : 'Paper'}
                    </span>
                    {activeBrokerLabel && (
                      <span className="bot-meta-item">
                        {activeBrokerCredential?.id ? (
                          <Link
                            className="bot-meta-account-link"
                            to={`/brokerage-accounts/${encodeURIComponent(activeBrokerCredential.id)}`}
                          >
                            {activeBrokerLabel}
                          </Link>
                        ) : (
                          activeBrokerLabel
                        )}
                      </span>
                    )}
                  </div>

                  {botDetails.description && (
                    <div className="bot-description-text">{botDetails.description}</div>
                  )}
                </div>

                <div className="bot-details-action-row">
                  <BotStrategyActionButton
                    actionType="editStrategy"
                    size="compact"
                    label="Ask AI"
                    onClick={handleAskAi}
                    type="button"
                  />
                  <BotStrategyActionButton
                    actionType="shareStrategy"
                    size="compact"
                    label={isLoadingMarketplaceListing ? 'Checking share status' : 'Share'}
                    onClick={openBackingStrategyShare}
                    type="button"
                    disabled={isLoadingMarketplaceListing}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      )}

      {!isLoadingData && botDetails && operationalBanner && (
        <Container className="fade-in-delayed bot-operational-banner">
          {renderBotStatusPanel(operationalBanner)}
        </Container>
      )}

      {showPerformanceSection && (
        <Container className="fade-in-delayed">
          <section className="bot-performance-surface slide-in-up" aria-label="Performance">
            {botDetails?.last_updated && (
              <div className="bot-performance-updated">
                Updated {moment(botDetails.last_updated).format('MMM D, YYYY h:mm a')}
              </div>
            )}

            <div className="bot-account-ledger">
              {latestPortfolioValue !== null || latestCash !== null ? (
                <>
                  <div className="bot-account-row fade-in">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Total value of your account.</Tooltip>}
                    >
                      <div className="account-stats-title">
                        Account Value <FontAwesomeIcon icon={faInfoCircle} />
                      </div>
                    </OverlayTrigger>
                    <div className="account-stats-value">
                      {latestPortfolioValue === null ? (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-2" />
                          Not available yet
                          <div className="small mt-1">
                            Account value will appear once trading begins
                          </div>
                        </div>
                      ) : (
                        <div className="account-metric-value-line">
                          <NumericFormat
                            value={latestPortfolioValue}
                            displayType="text"
                            thousandSeparator
                            prefix="$"
                            decimalScale={2}
                          />
                          {portfolioValueChange ? (
                            <span className={`account-metric-delta ${portfolioChangeClass}`}>
                              {formatSignedCurrencyDelta(portfolioValueChange.amount)}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bot-account-row fade-in-delayed">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Uninvested cash in the account.</Tooltip>}
                    >
                      <div className="account-stats-title">
                        Cash <FontAwesomeIcon icon={faInfoCircle} />
                      </div>
                    </OverlayTrigger>
                    <div className="account-stats-value">
                      {latestCash === null ? (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-2" />
                          Not available yet
                          <div className="small mt-1">
                            Cash balance will appear once initial deposit is processed
                          </div>
                        </div>
                      ) : (
                        <NumericFormat
                          value={latestCash}
                          displayType="text"
                          thousandSeparator
                          prefix="$"
                          decimalScale={2}
                        />
                      )}
                    </div>
                  </div>

                  <div className="bot-account-row fade-in-delayed">
                    <div className="account-stats-title">Return</div>
                    <div className={`account-stats-value ${returnValueClass}`}>
                      {displayedReturnPercent === null ? (
                        <div className="text-muted">Not available yet</div>
                      ) : (
                        <div className="account-metric-value-line">
                          <span className={`account-return-value ${returnValueClass}`}>
                            {formatLiveReturnPercentage(displayedReturnPercent)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {['Account Value', 'Cash', 'Return'].map((label) => (
                    <div
                      key={label}
                      className="bot-account-row bot-account-row-placeholder fade-in"
                    >
                      <div className="account-stats-title">{label}</div>
                      <div
                        className="account-stats-value text-muted"
                        aria-label={`${label} not available`}
                      >
                        --
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {botDetails?.botDetailsTelemetry?.account?.updatedAt && (
              <div className="bot-data-freshness">
                Account updated{' '}
                {moment(botDetails.botDetailsTelemetry.account.updatedAt).format(
                  'MMM D, YYYY h:mm a',
                )}
              </div>
            )}

            <div className="chart-section slide-in-up">
              <BotResultsChart
                data={chartData.length ? chartData : null}
                frequency={timeframesMap[timeframe]?.frequency || 'day'}
                isLoading={isLoadingChart}
                trendDirection={trendDirection}
                chartHeight={340}
                emptyTitle={chartEmptyTitle}
                emptyDescription={chartEmptyDescription}
                markers={chartMarkers}
                cashFlowAvailability={cashFlowAvailability}
              />
            </div>

            {selectableTimeframes.length > 0 && (
              <div className="timeframe-scrollbar-centered fade-in-delayed">
                {selectableTimeframes.map((tf) => {
                  const isSelected = timeframe === tf;
                  const label = timeframesMap[tf]?.label || tf;
                  return (
                    <button
                      key={tf}
                      className={`timeframe-button ${isSelected ? 'selected' : ''}`}
                      onPointerDown={(event) => {
                        if (typeof event.button !== 'number' || event.button === 0) {
                          selectChartTimeframe(tf);
                        }
                      }}
                      onClick={(event) => {
                        if (event.detail === 0) {
                          selectChartTimeframe(tf);
                        }
                      }}
                      type="button"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </Container>
      )}

      {showActivitySection && (
        <Container className="fade-in-delayed">
          {
            <Row className="fade-in">
              <Col>
                <div className="bot-detail-section-title">Positions</div>
                {isAuthenticated ? (
                  <BotPositionsList positions={botPositions} />
                ) : (
                  <Card className="bot-details-locked-content-card">
                    <Card.Body className="text-center">
                      <FontAwesomeIcon icon={faLock} size="2x" className="mb-3" />
                      <h4>Current Positions Hidden</h4>
                      <p>Log in to view this bot's current positions and trading activity.</p>
                      <Button variant="primary" onClick={handleLoginToBotDetails} className="mt-2">
                        Log In to View Positions
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          }

          {(deploymentPositionsUpdatedAt ||
            botDetails?.botDetailsTelemetry?.positions?.updatedAt) && (
            <div className="bot-data-freshness bot-data-freshness-positions">
              Positions updated{' '}
              {moment(
                deploymentPositionsUpdatedAt ||
                  botDetails?.botDetailsTelemetry?.positions?.updatedAt,
              ).format('MMM D, YYYY h:mm a')}
            </div>
          )}

          {isOwner && isAuthenticated && (deploymentSummary?.id || deploymentIdFromUrl) && (
            <Row className="mt-4 fade-in">
              <Col>
                <BotOrdersList
                  orders={deploymentOrders}
                  isLoading={isLoadingOrders}
                  error={ordersError}
                  lastUpdated={deploymentOrdersMeta.lastUpdated}
                  source={deploymentOrdersMeta.source}
                  onRefresh={refreshDeploymentLiveState}
                />
              </Col>
            </Row>
          )}
        </Container>
      )}

      {!isLoadingData && botDetails && (
        <Container className="fade-in-delayed bot-owner-controls">
          {renderOwnerSettings()}
          {renderOwnerDiagnostics()}
          {renderDangerZone()}
        </Container>
      )}

      {shareStrategy && (
        <ShareStrategyModal
          show={Boolean(shareStrategy)}
          onHide={closeShareStrategy}
          strategyName={shareStrategy.name || 'Untitled Strategy'}
          strategyId={shareStrategy.strategyId || shareStrategy.id || null}
          getApiToken={getApiToken}
          isPublic={Boolean(shareStrategy.marketplace?.isInMarketplace)}
          shareUrl={`${window.location.origin}/ai-bot-builder/${shareStrategy.aiStrategyId}`}
          onMakePublic={makeBackingStrategyPublic}
          onMakePrivate={makeBackingStrategyPrivate}
          isInMarketplace={Boolean(shareStrategy.marketplace?.isInMarketplace)}
          marketplaceListingId={shareStrategy.marketplace?.marketplaceListingId || null}
          existingPrivateShare={existingPrivateShare}
          shareInfoLoading={shareInfoLoading}
          onRefreshShareInfo={refreshShareInfo}
        />
      )}

      <ToastContainer position="top-center">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={10000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === 'success'
                ? 'Success'
                : toastVariant.charAt(0).toUpperCase() + toastVariant.slice(1)}
            </strong>
            <small>Just now</small>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </PageShell>
  );
}
